'use client'

import type { Data } from '@repo/strapi-types'
import AppLink from '@/components/elementary/AppLink'
import { NavbarMenuLink } from '@/components/elementary/navbar/NavbarMenuLink'
import { Paragraph } from '@/components/typography/Paragraph'
import { Button } from '@/components/ui/button'
import {
	NavigationMenuContent,
	NavigationMenuItem,
	NavigationMenuTrigger,
} from '@/components/ui/navigation-menu'
import { cn } from '@/lib/styles'
import { NAVBAR_TEST_IDS } from '@/lib/test-ids'

interface CTAButtonsProps {
	login?: Data.Component<'utilities.link'> | null
	buttons?: Data.Component<'utilities.link'>[] | null
	dropdown?: Data.Component<'layout-utilities.dropdown-item'> | null
	minimal?: boolean
}

export function CTAButtons({ login, buttons, dropdown, minimal }: CTAButtonsProps) {
	return (
		<div className="flex items-center justify-end w-full">
			{login?.href && login?.label ? (
				<div className={cn('flex items-center lg:w-fit w-full min-h-8', minimal && 'mr-2')}>
					<AppLink
						href={login.href ?? '#'}
						size={minimal ? 'small' : null}
						variant={minimal ? 'secondary' : 'primaryLink'}
						data-test-id={NAVBAR_TEST_IDS.LOGIN_BUTTON}
					>
						{minimal ? (
							login.label
						) : (
							<Paragraph fontSize="bodyRegularMedium" className="text-inherit">
								{login.label}
							</Paragraph>
						)}
					</AppLink>
					{!minimal && <div className="mx-4 w-px bg-neutral-light-500 h-6" />}
				</div>
			) : null}

			<div className="flex lg:flex-row flex-col items-center gap-2 lg:w-fit w-full">
				{buttons && buttons.length > 0
					? buttons.map((primaryButton) =>
							primaryButton.href ? (
								<AppLink
									key={primaryButton.id}
									size="small"
									variant="primary"
									href={primaryButton.href}
									data-test-id={NAVBAR_TEST_IDS.PRIMARY_BUTTON}
								>
									{primaryButton.label}
								</AppLink>
							) : null,
						)
					: null}

				{dropdown?.label && dropdown.links && dropdown.links?.length > 0 ? (
					<NavigationMenuItem>
						<Button variant="secondary" size="small" asChild>
							<NavigationMenuTrigger className="[&>svg]:hidden">
								{dropdown.label}
							</NavigationMenuTrigger>
						</Button>
						<NavigationMenuContent
							className="min-w-61.5 p-4 border-none shadow-box divide-y divide-neutral-light-500 left-[unset] right-0"
							asChild
						>
							<ul>
								{dropdown?.links?.map((link) => (
									<li key={link.id} className="block py-2">
										<NavbarMenuLink
											link={link}
											data-test-id={NAVBAR_TEST_IDS.DROPDOWN_ITEM}
											isDropDownItem
										/>
									</li>
								))}
							</ul>
						</NavigationMenuContent>
					</NavigationMenuItem>
				) : null}
			</div>
		</div>
	)
}
